import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import CoachCheckoutModal from './CoachCheckoutModal';
import CoachSubscribeModal from './CoachSubscribeModal';
import { unsubscribeFromCoach } from '../services/nutritionService';
import ConfirmModal from './ConfirmModal';
import SEO from './SEO';
import { absoluteUrl, buildBreadcrumbs } from '../utils/seoConfig';
import { trackCoachInquiry } from '../utils/analytics';
import './CoachProfilePage.css';

const THEME_MAP = {
  classic: { accent: '#48bb78', bg: '#f0fff4' },
  modern: { accent: '#38b2ac', bg: '#e6fffa' },
  warm: { accent: '#ed8936', bg: '#fffaf0' },
  fresh: { accent: '#38a169', bg: '#f0fff4' },
  dark: { accent: '#a78bfa', bg: '#f5f3ff' },
};

const CoachProfilePage = () => {
  const { slug } = useParams();
  const [coach, setCoach] = useState(null);
  const [items, setItems] = useState([]);
  const [contentType, setContentType] = useState('programs');
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [connected, setConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [activeServiceId, setActiveServiceId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [connectMessage, setConnectMessage] = useState('');
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [checkoutProgram, setCheckoutProgram] = useState(null);
  const [services, setServices] = useState([]);
  const [subscribeService, setSubscribeService] = useState(null);
  const [expandedDescs, setExpandedDescs] = useState({});
  const [coachClientId, setCoachClientId] = useState(null);
  const [unsubscribing, setUnsubscribing] = useState(false);
  const [unsubscribeModalOpen, setUnsubscribeModalOpen] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const token = localStorage.getItem('token');

  const refreshConnection = useCallback(async () => {
    if (!token || !slug) {
      setConnected(false);
      setConnectionStatus(null);
      setActiveServiceId(null);
      setCoachClientId(null);
      return;
    }
    try {
      const r = await fetch(`${API_URL}/fitness/coaches/${slug}/connection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = r.ok ? await r.json() : { connected: false, status: null };
      setConnected(!!data.connected);
      setConnectionStatus(data.status ?? null);
      setActiveServiceId(data.activeServiceId || null);
      setCoachClientId(data.coachClientId || null);
    } catch {
      setConnected(false);
      setConnectionStatus(null);
      setActiveServiceId(null);
      setCoachClientId(null);
    }
  }, [API_URL, slug, token]);

  useEffect(() => {
    fetchCoachProfile();
  }, [slug, contentType, page]);

  useEffect(() => {
    refreshConnection();
  }, [refreshConnection]);

  const handleConnect = async () => {
    if (!token) { toast.error('Sign in to connect with this coach'); return; }
    setConnecting(true);
    try {
      const res = await fetch(`${API_URL}/fitness/coaches/${slug}/connect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: connectMessage || undefined })
      });
      const data = await res.json();
      if (res.ok) {
        setConnectionStatus(data.connectionStatus || 'pending');
        setShowConnectForm(false);
        setConnectMessage('');
        trackCoachInquiry(slug, 'connect');
        toast.success('Connection request sent! The coach will review your request.');
      }
      else toast.error(data.error || 'Failed to connect');
    } catch { toast.error('Failed to connect'); }
    finally { setConnecting(false); }
  };

  const runUnsubscribeFromCoach = async () => {
    if (!coachClientId) {
      toast.error('Could not find your coach connection. Try refreshing the page.');
      setUnsubscribeModalOpen(false);
      return;
    }
    setUnsubscribing(true);
    try {
      const data = await unsubscribeFromCoach(coachClientId);
      toast.success('You have unsubscribed.');
      if (data.stripe?.attempted && data.stripe?.cancelled && data.stripe?.canceledSubscriptionId) {
        toast.success('Recurring billing was canceled in Stripe.');
      }
      if (data.stripe?.attempted && !data.stripe?.cancelled && data.stripe?.error) {
        toast.error(`Stripe could not cancel billing: ${data.stripe.error}`);
      }
      setUnsubscribeModalOpen(false);
      await refreshConnection();
    } catch (err) {
      toast.error(err.message || 'Failed to unsubscribe');
    } finally {
      setUnsubscribing(false);
    }
  };

  const handleEnroll = async (programId) => {
    if (!token) { toast.error('Sign in to enroll'); return; }
    try {
      const res = await fetch(`${API_URL}/fitness/programs/${programId}/enroll`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || 'Failed to enroll'); return; }
      if (data.requiresPayment) {
        // Find the item to get the name
        const program = items.find(i => i._id === programId);
        setCheckoutProgram({
          programId,
          programName: program?.name || data.programName || 'Program',
          amount: data.amount,
          currency: data.currency || 'usd',
          coachAffiliateId: data.coachAffiliateId
        });
        return;
      }
      toast.success(`Enrolled in ${data.programName}!`);
    } catch { toast.error('Failed to enroll'); }
  };

  const fetchCoachProfile = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ type: contentType, page, limit: 20 });
      const response = await fetch(`${process.env.REACT_APP_API_URL}/fitness/coach/${slug}?${params}`);
      if (!response.ok) {
        if (response.status === 404) throw new Error('Coach not found');
        throw new Error('Failed to load coach profile');
      }
      const data = await response.json();
      setCoach(data.coach);
      setItems(data.items || []);
      setCounts(data.counts || {});
      setTotalPages(data.totalPages || 1);
      if (data.services) setServices(data.services);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !coach) {
    return (
      <div className="coach-profile-page">
        <div className="coach-profile-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading coach profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="coach-profile-page">
        <div className="coach-profile-error">
          <i className="fas fa-exclamation-circle"></i>
          <h3>{error}</h3>
          <Link to="/coaches" className="back-link"><i className="fas fa-arrow-left"></i> Back to Coaches</Link>
        </div>
      </div>
    );
  }

  if (!coach) return null;

  const theme = THEME_MAP[coach.theme] || THEME_MAP.classic;

  const coachJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: coach.name,
    description: coach.headline || coach.bio || `${coach.name} on FreshFitLab`,
    url: absoluteUrl(`/coach/${coach.slug || coach._id}`),
    image: coach.logoImage,
    jobTitle: coach.headline,
    knowsAbout: coach.specializations || ['Fitness coaching', 'Nutrition coaching'],
    sameAs: [
      coach.socialMedia?.instagram && `https://instagram.com/${coach.socialMedia.instagram}`,
      coach.socialMedia?.facebook && `https://facebook.com/${coach.socialMedia.facebook}`,
      coach.socialMedia?.twitter && `https://twitter.com/${coach.socialMedia.twitter}`,
      coach.socialMedia?.youtube && `https://youtube.com/${coach.socialMedia.youtube}`,
      coach.socialMedia?.tiktok && `https://tiktok.com/@${coach.socialMedia.tiktok}`,
    ].filter(Boolean),
    worksFor: {
      '@type': 'Organization',
      name: 'FreshFitLab',
      url: 'https://freshfitlab.io',
    },
  };

  return (
    <div className="coach-profile-page">
      <SEO
        title={`${coach.name} — Online Fitness & Nutrition Coach`}
        description={
          (coach.headline || coach.bio || '').slice(0, 200) ||
          `${coach.name} is a coach on FreshFitLab. View programs, workouts, and book sessions.`
        }
        canonical={`/coach/${coach.slug || coach._id}`}
        image={coach.logoImage}
        type="profile"
        jsonLd={[
          coachJsonLd,
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Coaches', url: '/coaches' },
            { name: coach.name, url: `/coach/${coach.slug || coach._id}` },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="coach-profile-hero" style={{ background: `linear-gradient(135deg, ${theme.accent}22 0%, ${theme.bg} 100%)` }}>
        <div className="coach-profile-hero-content">
          {coach.logoImage ? (
            <img src={coach.logoImage} alt={coach.name} className="coach-profile-avatar" />
          ) : (
            <div className="coach-profile-avatar-placeholder" style={{ background: theme.accent }}>
              {(coach.name || '?')[0].toUpperCase()}
            </div>
          )}
          <h1 className="coach-profile-name">{coach.name}</h1>
          {coach.headline && <p className="coach-profile-headline">{coach.headline}</p>}
          <div className="coach-profile-social">
            {coach.socialMedia?.instagram && <a href={`https://instagram.com/${coach.socialMedia.instagram}`} target="_blank" rel="noopener noreferrer"><i className="fab fa-instagram"></i></a>}
            {coach.socialMedia?.facebook && <a href={`https://facebook.com/${coach.socialMedia.facebook}`} target="_blank" rel="noopener noreferrer"><i className="fab fa-facebook"></i></a>}
            {coach.socialMedia?.twitter && <a href={`https://twitter.com/${coach.socialMedia.twitter}`} target="_blank" rel="noopener noreferrer"><i className="fab fa-twitter"></i></a>}
            {coach.socialMedia?.tiktok && <a href={`https://tiktok.com/@${coach.socialMedia.tiktok}`} target="_blank" rel="noopener noreferrer"><i className="fab fa-tiktok"></i></a>}
            {coach.website && <a href={coach.website} target="_blank" rel="noopener noreferrer"><i className="fas fa-globe"></i></a>}
          </div>
          {user && (
            <div className="coach-profile-actions">
              {connected ? (
                <span className="connected-badge"><i className="fas fa-check-circle"></i> Connected</span>
              ) : connectionStatus === 'pending' ? (
                <span className="pending-badge"><i className="fas fa-clock"></i> Request Pending</span>
              ) : showConnectForm ? (
                <div className="connect-form">
                  <p className="connect-form-hint">Introduce yourself to the coach (optional)</p>
                  <textarea
                    className="connect-message-input"
                    placeholder="Hi! I'm interested in your coaching services. Here's a bit about me..."
                    value={connectMessage}
                    onChange={e => setConnectMessage(e.target.value)}
                    maxLength={500}
                    rows={3}
                  />
                  <div className="connect-form-actions">
                    <button className="connect-send-btn" onClick={handleConnect} disabled={connecting} style={{ background: theme.accent }}>
                      {connecting ? 'Sending...' : 'Send Request'}
                    </button>
                    <button className="connect-cancel-btn" onClick={() => setShowConnectForm(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button className="connect-btn" onClick={() => setShowConnectForm(true)} style={{ background: theme.accent }}>
                  <i className="fas fa-user-plus"></i> Connect with Coach
                </button>
              )}
            </div>
          )}
        </div>
      </section>
      {coach.bio && (
        <section className="coach-profile-bio-section">
          <p className="coach-profile-bio">{coach.bio}</p>
        </section>
      )}

      {/* Services */}
      {services.length > 0 && (
        <section className="coach-services-section">
          <h2 className="coach-section-title">Coaching Services</h2>
          <div className="coach-services-grid">
            {services.map(svc => (
              <div key={svc._id} className="coach-service-card" style={{ borderTopColor: theme.accent }}>
                <div className="coach-service-tier">{svc.tier}</div>
                <h3 className="coach-service-name">{svc.name}</h3>
                {svc.description && (
                  <div className="coach-service-desc-wrap">
                    <p className={`coach-service-desc${expandedDescs[svc._id] ? ' expanded' : ''}`}>{svc.description}</p>
                    {svc.description.length > 120 && (
                      <button
                        className="coach-service-desc-toggle"
                        onClick={() => setExpandedDescs(prev => ({ ...prev, [svc._id]: !prev[svc._id] }))}
                      >
                        {expandedDescs[svc._id] ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>
                )}
                <div className="coach-service-price">
                  <span className="price-amount">${(svc.price / 100).toFixed(2)}</span>
                  <span className="price-interval">/{svc.billingInterval === 'monthly' ? 'mo' : 'one-time'}</span>
                </div>
                {svc.features?.length > 0 && (
                  <ul className="coach-service-features">
                    {svc.features.map((f, i) => <li key={i}><i className="fas fa-check"></i> {f}</li>)}
                  </ul>
                )}
                {svc.sessionFrequency !== 'none' && (
                  <p className="coach-service-sessions">
                    {svc.sessionFrequency} {svc.sessionType === 'both' ? '' : svc.sessionType?.replace('_', ' ')} sessions &middot; {svc.sessionDuration}min
                  </p>
                )}
                {user && connected && String(activeServiceId) === String(svc._id) ? (
                  <div className="coach-service-subscribed">
                    <span className="subscribed-badge"><i className="fas fa-check-circle"></i> Subscribed</span>
                    {svc.billingInterval === 'monthly' && (
                      <span className="billing-info">Renews monthly</span>
                    )}
                    <button
                      type="button"
                      className="coach-service-unsubscribe-btn"
                      onClick={() => setUnsubscribeModalOpen(true)}
                      disabled={unsubscribing || !coachClientId}
                    >
                      Unsubscribe
                    </button>
                  </div>
                ) : user && connected ? (
                  <button
                    className="coach-service-subscribe-btn"
                    style={{ background: theme.accent }}
                    onClick={() => setSubscribeService(svc)}
                  >
                    Subscribe
                  </button>
                ) : user && connectionStatus === 'pending' ? (
                  <p className="coach-service-connect-hint">Waiting for coach approval to subscribe</p>
                ) : user ? (
                  <p className="coach-service-connect-hint">Connect with this coach to subscribe</p>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Content Tabs */}
      <section className="coach-profile-content">
        <div className="coach-content-tabs">
          <button className={`coach-content-tab ${contentType === 'programs' ? 'active' : ''}`} onClick={() => { setContentType('programs'); setPage(1); }} style={contentType === 'programs' ? { borderColor: theme.accent, color: theme.accent } : {}}>
            <i className="fas fa-calendar-alt"></i> Programs
          </button>
          <button className={`coach-content-tab ${contentType === 'workouts' ? 'active' : ''}`} onClick={() => { setContentType('workouts'); setPage(1); }} style={contentType === 'workouts' ? { borderColor: theme.accent, color: theme.accent } : {}}>
            <i className="fas fa-list-ol"></i> Workouts
          </button>
          <button className={`coach-content-tab ${contentType === 'exercises' ? 'active' : ''}`} onClick={() => { setContentType('exercises'); setPage(1); }} style={contentType === 'exercises' ? { borderColor: theme.accent, color: theme.accent } : {}}>
            <i className="fas fa-running"></i> Exercises
          </button>
        </div>

        {loading ? (
          <div className="coach-content-loading">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
        ) : items.length === 0 ? (
          <div className="coach-content-empty">
            <p>No {contentType} published yet.</p>
          </div>
        ) : (
          <>
            <div className="coach-content-grid">
              {items.map(item => (
                <div key={item._id} className="coach-content-card">
                  <div className="coach-content-card-accent" style={{ background: theme.accent }} />
                  <div className="coach-content-card-body">
                    <h4>{item.name}</h4>
                    {item.description && <p className="coach-content-card-desc">{item.description}</p>}
                    <div className="coach-content-card-meta">
                      {item.difficulty && <span className="meta-tag">{item.difficulty}</span>}
                      {item.duration && <span className="meta-tag">{item.duration} min</span>}
                      {item.durationWeeks && <span className="meta-tag">{item.durationWeeks} weeks</span>}
                      {item.category && <span className="meta-tag">{item.category}</span>}
                      {item.exercises?.length > 0 && <span className="meta-tag">{item.exercises.length} exercises</span>}
                      {item.accessType === 'invite_only' && <span className="meta-tag members-only">Members Only</span>}
                    </div>
                    {contentType === 'programs' && user && (
                      <button className="enroll-btn" onClick={() => handleEnroll(item._id)} style={{ background: theme.accent }}>
                        Enroll — Free
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="coach-pagination">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <i className="fas fa-chevron-left"></i> Prev
                </button>
                <span>Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  Next <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {checkoutProgram && (
        <CoachCheckoutModal
          coachAffiliateId={checkoutProgram.coachAffiliateId}
          amount={checkoutProgram.amount}
          currency={checkoutProgram.currency}
          programName={checkoutProgram.programName}
          programId={checkoutProgram.programId}
          onSuccess={(data) => {
            setCheckoutProgram(null);
            toast.success(`Enrolled in ${checkoutProgram.programName}!`);
          }}
          onClose={() => setCheckoutProgram(null)}
        />
      )}

      {subscribeService && (
        <CoachSubscribeModal
          service={subscribeService}
          coachSlug={slug}
          onSuccess={() => {
            setActiveServiceId(subscribeService._id);
            setSubscribeService(null);
            toast.success(`Subscribed to ${subscribeService.name}!`);
          }}
          onClose={() => setSubscribeService(null)}
        />
      )}

      <ConfirmModal
        isOpen={unsubscribeModalOpen}
        title="Unsubscribe from this coach?"
        message="This ends your connection, cancels the coaching subscription when applicable, and removes their workout assignments from My Workouts."
        confirmLabel="Unsubscribe"
        cancelLabel="Cancel"
        danger
        loading={unsubscribing}
        loadingLabel="Unsubscribing…"
        onConfirm={runUnsubscribeFromCoach}
        onCancel={() => !unsubscribing && setUnsubscribeModalOpen(false)}
      />
    </div>
  );
};

export default CoachProfilePage;
